/**
 * Global tooltip delegate -- intercepts every native ``title=``
 * attribute on the page and renders it through the same custom popup
 * that ``<Tooltip>`` uses.  Mounted once near the top of the App tree,
 * removes the need to refactor 13 component files individually.
 *
 * Why this exists
 * ---------------
 * Native ``title`` tooltips have several pain points (see ``Tooltip``
 * for the long story): 500-1500 ms delay, no multi-line, suppressed
 * by some accessibility tools / extensions, hidden by overflow in
 * some browser combinations.  The dashboard had ``title=`` strewn
 * across ~40 places (pod-room stats, portfolio, leaderboard, news
 * ticker, ...).  Refactoring each was high-touch; this delegate
 * achieves the same UX with one mount.
 *
 * How it works
 * ------------
 *   1. ``mouseover`` listener on ``document.body`` catches every
 *      hover bubbling up.  ``closest('[title]')`` walks up to the
 *      nearest element with a title attribute.
 *   2. The original title is stashed in ``data-orig-title`` and the
 *      live ``title`` attribute is removed so the browser doesn't
 *      race us with its native popup.
 *   3. After a 120 ms latch (matches the in-component ``Tooltip``)
 *      a popover is appended to ``document.body``, positioned next
 *      to the trigger.
 *   4. ``mouseout`` removes the popover and restores ``title``.  We
 *      need to restore it so a) screen-readers still see it,
 *      b) re-hovering still triggers the delegate.
 *
 * Multi-line: titles may use ``\n`` separators -- we split and render
 * one line per row, matching the ``content`` array shape of
 * ``Tooltip``.
 *
 * Edge cases handled:
 *   * Mouse moves between trigger and child -- ``mouseout`` to a
 *     descendant doesn't dismiss the popup (relatedTarget check).
 *   * Trigger is removed from the DOM mid-hover -- ``MutationObserver``
 *     cleanup hides the popup so it doesn't dangle.
 *   * Scroll while hovered -- we re-anchor on scroll/resize so the
 *     popup stays glued to the trigger.
 */
import { useEffect } from 'react';

const SHOW_DELAY_MS = 120;
const OFFSET_PX = 8;
const MAX_WIDTH = 320;

export function TooltipDelegate() {
  useEffect(() => {
    let popup: HTMLDivElement | null = null;
    let trigger: HTMLElement | null = null;
    let showTimer: number | null = null;
    let observer: MutationObserver | null = null;

    const reposition = () => {
      if (!popup || !trigger) return;
      const r = trigger.getBoundingClientRect();
      const below = window.innerHeight - r.bottom > 80;
      const top = below ? r.bottom + OFFSET_PX : r.top - OFFSET_PX;
      const halfW = MAX_WIDTH / 2;
      let centreX = r.left + r.width / 2;
      centreX = Math.max(halfW + 8, Math.min(window.innerWidth - halfW - 8, centreX));
      popup.style.left = `${centreX}px`;
      popup.style.top = `${top}px`;
      popup.style.transform = below ? 'translate(-50%, 0)' : 'translate(-50%, -100%)';
    };

    const hide = () => {
      if (showTimer !== null) {
        window.clearTimeout(showTimer);
        showTimer = null;
      }
      if (popup) {
        popup.remove();
        popup = null;
      }
      if (trigger) {
        const orig = trigger.getAttribute('data-orig-title');
        if (orig !== null) {
          trigger.setAttribute('title', orig);
          trigger.removeAttribute('data-orig-title');
        }
        trigger = null;
      }
      if (observer) {
        observer.disconnect();
        observer = null;
      }
    };

    const show = (el: HTMLElement, text: string) => {
      const lines = text.split('\n').filter((l) => l.length > 0);
      if (lines.length === 0) return;
      popup = document.createElement('div');
      popup.setAttribute('role', 'tooltip');
      popup.style.cssText = [
        'position: fixed',
        'background: #161b22',
        'color: #e6edf3',
        'border: 1px solid #30363d',
        'border-radius: 6px',
        'padding: 6px 10px',
        'font-size: 11px',
        'line-height: 1.45',
        `max-width: ${MAX_WIDTH}px`,
        'box-shadow: 0 8px 24px rgba(0, 0, 0, 0.55)',
        'white-space: pre-wrap',
        'pointer-events: none',
        'z-index: 9999',
      ].join(';');
      for (const line of lines) {
        const row = document.createElement('div');
        row.textContent = line;
        popup.appendChild(row);
      }
      document.body.appendChild(popup);
      trigger = el;
      reposition();
      // If the trigger gets removed from the DOM while hovered, hide.
      observer = new MutationObserver(() => {
        if (trigger && !document.contains(trigger)) hide();
      });
      observer.observe(document.body, { childList: true, subtree: true });
    };

    const onOver = (e: MouseEvent) => {
      const target = (e.target as HTMLElement | null)?.closest('[title]');
      if (!(target instanceof HTMLElement)) return;
      const text = target.getAttribute('title');
      if (!text) return;
      // Hide any prior popup -- only one tooltip at a time.
      if (trigger && trigger !== target) hide();
      if (trigger === target) return; // already armed for this trigger
      // Suppress native browser tooltip by stashing + clearing title.
      target.setAttribute('data-orig-title', text);
      target.removeAttribute('title');
      trigger = target;
      if (showTimer !== null) window.clearTimeout(showTimer);
      showTimer = window.setTimeout(() => show(target, text), SHOW_DELAY_MS);
    };

    const onOut = (e: MouseEvent) => {
      if (!trigger) return;
      // Ignore mouseouts to descendants of the trigger -- we still
      // want the popup to stay visible while the user moves over
      // child elements.
      const related = e.relatedTarget as Node | null;
      if (related && trigger.contains(related)) return;
      hide();
    };

    // Touch devices fire ``touchstart`` instead of mouseover -- and
    // on iOS Safari, the synthesized ``mouseover`` that follows
    // immediately gets paired with a ``mouseout`` on the very next
    // ``touchstart``, so the popup flickers and vanishes.  Handle
    // taps explicitly: first tap on a trigger shows the popup, tap
    // anywhere else (or on the same trigger again) hides it.
    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType !== 'touch' && e.pointerType !== 'pen') return;
      const target = (e.target as HTMLElement | null)?.closest('[title], [data-orig-title]');
      if (!(target instanceof HTMLElement)) {
        // Tap on something without a tooltip -- dismiss the open one.
        if (trigger) hide();
        return;
      }
      // Tap on the already-armed trigger -- toggle off.
      if (trigger === target && popup) {
        hide();
        return;
      }
      // Same flow as mouseover, but show immediately (touch users
      // already committed by tapping; no need to wait 120 ms).
      const text =
        target.getAttribute('title') ?? target.getAttribute('data-orig-title');
      if (!text) return;
      if (trigger && trigger !== target) hide();
      if (target.hasAttribute('title')) {
        target.setAttribute('data-orig-title', text);
        target.removeAttribute('title');
      }
      trigger = target;
      if (showTimer !== null) window.clearTimeout(showTimer);
      show(target, text);
    };

    const onScrollOrResize = () => {
      if (popup && trigger) reposition();
    };

    document.body.addEventListener('mouseover', onOver, { capture: true });
    document.body.addEventListener('mouseout', onOut, { capture: true });
    document.body.addEventListener('pointerdown', onPointerDown, { capture: true });
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);

    return () => {
      hide();
      document.body.removeEventListener('mouseover', onOver, { capture: true });
      document.body.removeEventListener('mouseout', onOut, { capture: true });
      document.body.removeEventListener('pointerdown', onPointerDown, { capture: true });
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, []);

  return null;
}
